import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, MapPin, Users, Phone, Mail, MessageCircle, Map as MapIcon, Trophy, Loader2, Timer } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import api from "@/lib/api";
import { HERO_IMG, inr, longDate, ageLabel } from "./runShared";
import { RichHtml, RUN_STYLES } from "./RunFormParts";

const REG = "/run/register";

function Countdown({ ymd }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);
  const diff = new Date(`${ymd}T05:00:00+05:30`).getTime() - now;
  if (!ymd || diff <= 0) return null;
  const days = Math.floor(diff / 86400000), hours = Math.floor((diff % 86400000) / 3600000), mins = Math.floor((diff % 3600000) / 60000);
  return (
    <div className="flex gap-3" data-testid="ml-countdown">
      {[[days, "Days"], [hours, "Hours"], [mins, "Mins"]].map(([v, l]) => (
        <div key={l} className="bg-white/10 border border-white/15 backdrop-blur-md rounded-xl px-4 py-2 text-center min-w-[74px]">
          <div className="run-display text-3xl leading-none">{String(v).padStart(2, "0")}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1">{l}</div>
        </div>
      ))}
    </div>
  );
}

function TopNav({ links, cta }) {
  const [solid, setSolid] = useState(false);
  useEffect(() => { const f = () => setSolid(window.scrollY > 40); f(); window.addEventListener("scroll", f); return () => window.removeEventListener("scroll", f); }, []);
  return (
    <nav className={`fixed top-0 inset-x-0 z-40 transition-colors duration-300 ${solid ? "bg-[#08363A]/95 backdrop-blur-md shadow-lg" : "bg-transparent"}`} data-testid="ml-topnav">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4 text-white">
        <a href="#top" className="run-display text-xl uppercase tracking-wide inline-flex items-center gap-2" data-testid="ml-nav-brand"><Timer size={18} className="text-[#F26419]" />GS Mandal Run</a>
        <div className="hidden lg:flex items-center gap-6 text-[12px] uppercase tracking-[0.14em] font-semibold">
          {links.map(([id, label]) => <a key={id} href={`#${id}`} className="text-white/80 hover:text-[#F26419] transition-colors" data-testid={`ml-nav-${id}`}>{label}</a>)}
        </div>
        <Link to={REG} className="bg-[#F26419] hover:bg-white hover:text-[#08363A] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors" data-testid="ml-nav-register">{cta}</Link>
      </div>
    </nav>
  );
}

function Hero({ landing, event, runs }) {
  const title = (landing.hero.title || event.name || "GS Mandal Run").trim();
  const words = title.split(/\s+/);
  const last = words.length > 1 ? words.pop() : "";
  const next = [...runs].sort((a, b) => a.event_date.localeCompare(b.event_date))[0];
  return (
    <header id="top" className="relative min-h-[92vh] flex items-end overflow-hidden bg-[#08363A] text-white run-stripe" data-testid="ml-hero">
      <img src={landing.banner_url || event.banner_url || HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" data-testid="ml-hero-image" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#08363A] via-[#08363A]/70 to-[#08363A]/30" />
      <div className="relative max-w-6xl mx-auto w-full px-5 sm:px-8 pb-16 pt-32">
        {landing.hero.event_date_text && (
          <div className="run-rise inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-semibold text-[#F26419] bg-white/10 px-3 py-1.5 rounded-full" data-testid="ml-hero-date"><CalendarDays size={13} />{landing.hero.event_date_text}</div>
        )}
        <h1 className="run-rise run-display uppercase text-5xl sm:text-6xl lg:text-8xl leading-[0.88] mt-5 max-w-4xl" data-testid="ml-hero-title">
          {words.join(" ")}{last && <> <span className="text-[#F26419]">{last}</span></>}
        </h1>
        {(landing.hero.tagline || event.description) && <p className="run-rise mt-6 text-sm sm:text-lg text-white/80 max-w-2xl whitespace-pre-line" data-testid="ml-hero-tagline">{landing.hero.tagline || event.description}</p>}
        <div className="run-rise mt-8 flex flex-wrap items-center gap-4">
          <Link to={REG} className="inline-flex items-center gap-2 bg-[#F26419] hover:bg-white hover:text-[#08363A] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-full transition-colors" data-testid="ml-hero-register">{landing.hero.cta_label || "Register Now"}<ArrowRight size={16} /></Link>
          {runs.length > 0 && landing.categories.visible && <a href="#categories" className="text-sm font-semibold underline underline-offset-4 text-white/85 hover:text-white" data-testid="ml-hero-categories">View distances</a>}
        </div>
        <div className="mt-10 flex flex-wrap items-end justify-between gap-6">
          <div className="flex flex-wrap gap-3">{runs.map((r) => <span key={r.id} className="run-display text-2xl px-4 py-1.5 rounded-full border border-white/25 bg-white/5">{r.distance_km} KM</span>)}</div>
          {next && <Countdown ymd={next.event_date} />}
        </div>
      </div>
    </header>
  );
}

const SecHead = ({ eyebrow, heading }) => (
  <div className="mb-10">
    <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F26419]">{eyebrow}</div>
    <h2 className="run-display uppercase text-4xl sm:text-5xl text-[#08363A] leading-none mt-2">{heading}</h2>
  </div>
);

function Categories({ sec, runs }) {
  return (
    <section id="categories" className="py-20 sm:py-28 bg-white" data-testid="ml-categories">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SecHead eyebrow="Pick your race" heading={sec.heading} />
        {sec.intro && <p className="-mt-6 mb-10 text-[#5B5B5B] max-w-2xl">{sec.intro}</p>}
        {runs.length === 0 ? <p className="text-[#5B5B5B]" data-testid="ml-no-runs">Run categories will be announced soon.</p> : (
          <div className="grid md:grid-cols-2 gap-6">
            {runs.map((r) => (
              <div key={r.id} className="run-rise group relative border-2 border-[#E9E2D2] hover:border-[#F26419] rounded-3xl p-7 bg-[#FAF6EB] transition-[border-color,transform] duration-300 hover:-translate-y-1" data-testid={`ml-run-${r.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="run-display text-7xl sm:text-8xl leading-none text-[#0D5C63]">{r.distance_km}<span className="text-3xl text-[#F26419] ml-1">KM</span></div>
                  <div className="text-right"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8478]">Entry fee</div><div className="run-display text-4xl text-[#08363A]" data-testid={`ml-run-fee-${r.id}`}>{inr(r.fee)}</div></div>
                </div>
                <div className="mt-3 text-lg font-semibold">{r.name}</div>
                <RichHtml html={r.description} className="mt-2 text-sm text-[#5B5B5B]" />
                <div className="mt-5 grid grid-cols-2 gap-3 text-xs text-[#3a362f]">
                  <div className="flex items-center gap-2"><CalendarDays size={14} className="text-[#F26419]" />{longDate(r.event_date)}</div>
                  {r.reporting_time && <div className="flex items-center gap-2"><Clock size={14} className="text-[#F26419]" />Reporting {r.reporting_time}</div>}
                  <div className="flex items-center gap-2"><Users size={14} className="text-[#F26419]" />Age {ageLabel(r)}</div>
                  {r.slots_left != null && <div className="flex items-center gap-2 font-semibold" data-testid={`ml-run-slots-${r.id}`}><Trophy size={14} className="text-[#F26419]" />{r.slots_left} slots left</div>}
                </div>
                {r.is_open ? (
                  <Link to={`${REG}?run=${r.id}`} className="mt-6 inline-flex items-center gap-2 bg-[#08363A] group-hover:bg-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-6 py-3 rounded-full transition-colors" data-testid={`ml-run-register-${r.id}`}>Register for {r.distance_km} KM<ArrowRight size={14} /></Link>
                ) : <div className="mt-6 inline-block text-[11px] uppercase tracking-wider font-bold text-[#B02020] bg-[#FDECEC] px-3 py-2 rounded-full">{r.closed_reason}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Schedule({ sec }) {
  return (
    <section id="schedule" className="py-20 sm:py-28 bg-[#08363A] text-white run-stripe" data-testid="ml-schedule">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="text-[11px] uppercase tracking-[0.3em] font-semibold text-[#F26419]">Race day</div>
        <h2 className="run-display uppercase text-4xl sm:text-5xl leading-none mt-2 mb-12">{sec.heading}</h2>
        <ol className="relative border-l-2 border-white/15 ml-3 space-y-8">
          {sec.items.map((it, i) => (
            <li key={i} className="run-rise pl-8 relative" data-testid={`ml-schedule-item-${i}`}>
              <span className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-[#F26419] ring-4 ring-[#08363A]" />
              <div className="run-display text-2xl text-[#F26419]">{it.time}</div>
              <div className="text-lg font-semibold">{it.title}</div>
              {it.detail && <div className="text-sm text-white/70 mt-1">{it.detail}</div>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function RichSection({ id, sec, eyebrow, dark, extra }) {
  return (
    <section id={id} className={`py-20 sm:py-28 ${dark ? "bg-[#F26419] text-white" : "bg-[#FAF6EB]"}`} data-testid={`ml-${id}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.4fr] gap-10">
        <div>
          <div className={`text-[11px] uppercase tracking-[0.3em] font-semibold ${dark ? "text-white/80" : "text-[#F26419]"}`}>{eyebrow}</div>
          <h2 className={`run-display uppercase text-4xl sm:text-5xl leading-none mt-2 ${dark ? "" : "text-[#08363A]"}`}>{sec.heading}</h2>
          {extra}
        </div>
        <RichHtml html={sec.html} className={`text-base leading-relaxed ${dark ? "prose-invert text-white" : "text-[#3a362f]"}`} testid={`ml-${id}-body`} />
      </div>
    </section>
  );
}

function Faqs({ sec }) {
  return (
    <section id="faqs" className="py-20 sm:py-28 bg-white" data-testid="ml-faqs">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <SecHead eyebrow="Good to know" heading={sec.heading} />
        <Accordion type="single" collapsible className="border-t border-[#E9E2D2]">
          {sec.items.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`} className="border-b border-[#E9E2D2]" data-testid={`ml-faq-${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold text-[#08363A] hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent><RichHtml html={f.a} className="text-sm text-[#5B5B5B] pb-2" /></AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Contact({ sec }) {
  const wa = (sec.whatsapp || "").replace(/\D/g, "");
  const items = [
    sec.phone && [Phone, "Call", sec.phone, `tel:${sec.phone.replace(/\s/g, "")}`],
    wa && [MessageCircle, "WhatsApp", sec.whatsapp, `https://wa.me/${wa.length === 10 ? `91${wa}` : wa}`],
    sec.email && [Mail, "Email", sec.email, `mailto:${sec.email}`],
    sec.address && [MapPin, "Address", sec.address, ""],
  ].filter(Boolean);
  return (
    <section id="contact" className="py-20 sm:py-28 bg-[#FAF6EB]" data-testid="ml-contact">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <SecHead eyebrow="Get in touch" heading={sec.heading} />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(([Icon, label, value, href]) => {
            const body = (<><Icon size={20} className="text-[#F26419]" /><div className="text-[10px] uppercase tracking-[0.2em] text-[#8a8478] mt-3">{label}</div><div className="text-sm font-semibold text-[#08363A] mt-1 whitespace-pre-line break-words">{value}</div></>);
            return href
              ? <a key={label} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="bg-white border border-[#E9E2D2] hover:border-[#0D5C63] rounded-2xl p-5 transition-colors" data-testid={`ml-contact-${label.toLowerCase()}`}>{body}</a>
              : <div key={label} className="bg-white border border-[#E9E2D2] rounded-2xl p-5" data-testid={`ml-contact-${label.toLowerCase()}`}>{body}</div>;
          })}
        </div>
      </div>
    </section>
  );
}

export default function MarathonLandingPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/public/marathon/landing").then(({ data }) => {
      setData(data);
      const t = data.landing.hero.title || data.event.name;
      if (t) document.title = `${t} · GS Mandal`;
    }).catch(() => setData({ error: true }));
  }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#08363A] text-white/70 text-sm" data-testid="ml-page-loading"><Loader2 className="animate-spin mr-2" size={16} />Loading…</div>;
  if (data.error) return <div className="min-h-screen flex items-center justify-center bg-[#FAF6EB] text-sm text-[#5B5B5B]" data-testid="ml-page-error">Could not load the page. Please try again.</div>;

  const { landing, event, runs } = data;
  const show = {
    about: landing.about.visible && landing.about.html,
    categories: landing.categories.visible,
    schedule: landing.schedule.visible && landing.schedule.items.length > 0,
    route: landing.route.visible && (landing.route.html || landing.route.map_url),
    prizes: landing.prizes.visible && landing.prizes.html,
    faqs: landing.faqs.visible && landing.faqs.items.length > 0,
    contact: landing.contact.visible && (landing.contact.phone || landing.contact.email || landing.contact.whatsapp || landing.contact.address),
  };
  const links = [["about", "About"], ["categories", "Distances"], ["schedule", "Schedule"], ["route", "Route"], ["prizes", "Prizes"], ["faqs", "FAQs"], ["contact", "Contact"]].filter(([k]) => show[k]);
  const cta = landing.hero.cta_label || "Register Now";

  return (
    <div className="run-page bg-[#FAF6EB] text-[#1A1A1A] scroll-smooth" data-testid="marathon-landing-page">
      <style>{`${RUN_STYLES} html{scroll-behavior:smooth} section[id]{scroll-margin-top:64px}`}</style>
      <TopNav links={links} cta={cta} />
      <Hero landing={landing} event={event} runs={runs} />
      {show.about && <RichSection id="about" sec={landing.about} eyebrow="The story" />}
      {show.categories && <Categories sec={landing.categories} runs={runs} />}
      {show.schedule && <Schedule sec={landing.schedule} />}
      {show.route && (
        <RichSection id="route" sec={landing.route} eyebrow="Where" extra={landing.route.map_url && (
          <a href={landing.route.map_url} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#08363A] hover:bg-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-full transition-colors" data-testid="ml-route-map"><MapIcon size={14} />Open in Google Maps</a>
        )} />
      )}
      {show.prizes && <RichSection id="prizes" sec={landing.prizes} eyebrow="Rewards" dark />}
      {show.faqs && <Faqs sec={landing.faqs} />}
      {show.contact && <Contact sec={landing.contact} />}
      <section className="bg-[#08363A] text-white run-stripe py-16" data-testid="ml-final-cta">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <h2 className="run-display uppercase text-4xl sm:text-5xl leading-none">See you at the <span className="text-[#F26419]">start line.</span></h2>
          <Link to={REG} className="self-start inline-flex items-center gap-2 bg-[#F26419] hover:bg-white hover:text-[#08363A] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-full transition-colors" data-testid="ml-final-register">{cta}<ArrowRight size={16} /></Link>
        </div>
      </section>
      <footer className="bg-[#052628] text-white/50 text-[11px] text-center py-5">© Gramaudyogik Shikshan Mandal · Chhatrapati Sambhaji Nagar</footer>
    </div>
  );
}
